import _ from 'lodash';

import { CLIENT_ROOT } from 'src/constants/paths';
import { TabState } from 'src/features/tabsState';

const urlRegexSplitter = new RegExp(/[?//,;&:#$+=]/);

const generateTabsByPath = (path) => {
  const tabs = {
    SETTINGS: {
      pathName: `${CLIENT_ROOT}/settings`,
      getLabel: () => 'Settings',
    },
    PATIENTS: {
      pathName: `${CLIENT_ROOT}/patients`,
      getLabel: () => {
        if (path.includes('details')) {
          const pathSegments = path.split(urlRegexSplitter);
          return `Member Details: ${pathSegments[4]}`;
        }

        return 'Members List';
      },
    },
    MANAGE_CLIENTS: {
      pathName: `${CLIENT_ROOT}/manage-clients`,
      getLabel: () => 'Manage Clients',
    },
    DASHBOARD: {
      pathName: `${CLIENT_ROOT}/dashboard`,
      getLabel: () => 'Perry Dashboard',
    },
    CARE_ORCHESTRATION: {
      pathName: `${CLIENT_ROOT}/care-orchestration`,
      getLabel: () => 'Mindspan Dashboard',
    },
    REPORTS: {
      pathName: `${CLIENT_ROOT}/reports`,
      getLabel: () => 'Reports',
    },
    LEAD_CREATION: {
      pathName: `${CLIENT_ROOT}/lead-creation`,
      getLabel: () => 'Lead Creation',
    },
    LEADS: {
      pathName: `${CLIENT_ROOT}/leads`,
      getLabel: () => 'Leads',
    },
    MANAGE_PROMPT_TEMPLATES: {
      pathName: `${CLIENT_ROOT}/manage-prompt-templates`,
      getLabel: () => 'Manage Prompt Templates',
    },
    TASK_TITLES: {
      pathName: `${CLIENT_ROOT}/task-titles`,
      getLabel: () => 'Task Title Manager',
    },
  };

  return tabs;
};

export const getTab = (path) => {
  if (!path) return { label: '', path };

  const tabs = generateTabsByPath(path);
  const tab = _.filter(Object.values(tabs), (t) => path.startsWith(t.pathName));

  return tab?.[0] || { getLabel: () => '', pathName: path };
};

export const getPathByLabel = (label, patientsPaths, settingsPath) => {
  if (label.startsWith('Settings')) return settingsPath;
  if (label.startsWith('Member Details')) {
    const parts = label.split(' ');
    const patientId = parts[2];
    return (
      patientsPaths?.[Number(patientId)]?.path || `${CLIENT_ROOT}/patients/details/${patientId}`
    );
  }
  if (label.startsWith('Manage Clients')) return `${CLIENT_ROOT}/manage-clients`;
  if (label.startsWith('Reports')) return `${CLIENT_ROOT}/reports`;
  if (label.startsWith('Lead Creation')) return `${CLIENT_ROOT}/lead-creation`;
  if (label === 'Leads') return `${CLIENT_ROOT}/leads`;
  if (label.startsWith('Manage Prompt Templates')) return `${CLIENT_ROOT}/manage-prompt-templates`;
  if (label.startsWith('Mindspan Dashboard')) return `${CLIENT_ROOT}/care-orchestration`;
  if (label.startsWith('Perry Dashboard')) return `${CLIENT_ROOT}/dashboard`;
  if (label.startsWith('Task Title Manager')) return `${CLIENT_ROOT}/task-titles`;
};

export const getTabStateByLabel = (label) => {
  if (label.startsWith('Settings')) return TabState.SETTINGS;
  if (label.startsWith('Manage Clients')) return TabState.MANAGE_CLIENTS;
  if (label.startsWith('Manage Prompt Templates')) return TabState.MANAGE_PROMPT_TEMPLATES;
  if (label.startsWith('Mindspan Dashboard')) return TabState.DASHBOARD;
  if (label.startsWith('Perry Dashboard')) return TabState.DASHBOARD;
  if (label.startsWith('Reports')) return TabState.REPORTS;
  if (label.startsWith('Lead Creation')) return TabState.LEAD_CREATION;
  if (label === 'Leads') return TabState.LEADS;
  if (label.startsWith('Task Title Manager')) return TabState.TASK_TITLES;
  return '';
};

// a function to retry loading a chunk to avoid chunk load error for out of date code
export const lazyRetry = (componentImport) =>
  new Promise((resolve, reject) => {
    // check if the window has already been refreshed
    const hasRefreshed = JSON.parse(
      window.sessionStorage.getItem('retry-lazy-refreshed') || 'false',
    );
    // try to import the component
    componentImport()
      .then((component) => {
        window.sessionStorage.setItem('retry-lazy-refreshed', 'false'); // success so reset the refresh
        resolve(component);
      })
      .catch((error) => {
        if (!hasRefreshed) {
          // not been refreshed yet
          window.sessionStorage.setItem('retry-lazy-refreshed', 'true'); // we are now going to refresh
          return window.location.reload(); // refresh the page
        }
        reject(error); // Default error behaviour as already tried refresh
      });
  });

export const getAllowedTabs = (permissions, isAdmin = false) => {
  const allowedTabs = [
    'Perry Dashboard',
    'Mindspan Dashboard',
    'Settings',
    'Member Details',
    'Manage Prompt Templates',
    'Lead Creation',
  ];
  if (permissions.canManageLeads || permissions.canCreatePatient) {
    allowedTabs.push('Leads');
  }
  if (permissions.canManageClients) {
    allowedTabs.push('Manage Clients');
  }
  if (permissions.canRequestReports) {
    allowedTabs.push('Reports');
  }
  if (isAdmin) {
    allowedTabs.push('Task Title Manager');
  }
  return allowedTabs;
};
